import { RegisterComponent } from '@/common/register-component';
import { Component, Element, h, Host, Prop, State, Watch } from '@stencil/core';
import type { NylasSchedulerConnector } from '../../..';
import type { ThemeConfig, Timeslot } from '@nylas/core';
import { NylasScheduling } from '../nylas-scheduling/nylas-scheduling';
import { capitalizeFirstLetter, debug, formatBookedEventDate } from '@/utils/utils';
import { LANGUAGE_CODE, LANGUAGE_CODE_TO_LOCALE_MAP, TIMEZONE_MAP } from '@/common/constants';
import i18next from '@/utils/i18n';

/**
 * The `nylas-selected-event-card` component is a UI component that displays the selected event card.
 *
 * @part nsec - The selected event card component.
 * @part nsec__card - The card component.
 * @part nsec__icon - The calendar icon.
 * @part nsec__date - The date selected.
 * @part nsec__time - The timeslot selected.
 * @part nsec__timezone - The timezone selected.
 */
@Component({
  tag: 'nylas-selected-event-card',
  styleUrl: 'nylas-selected-event-card.scss',
  shadow: true,
})
export class NylasSelectedEventCard {
  /**
   * The host element.
   * Used to manage the host element of the provider.
   */
  @Element() private host!: HTMLNylasSelectedEventCardElement;

  /**
   * @standalone
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: any;

  /**
   * The selected date.
   */
  @Prop({ attribute: 'selected-date' }) readonly selectedDate?: Date;

  /**
   * The selected time.
   */
  @Prop({ attribute: 'selected-timeslot' }) readonly selectedTimeslot?: Timeslot;

  /**
   * The selected timezone.
   */
  @Prop({ attribute: 'selected-timezone' }) readonly selectedTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

  /**
   * The selected language.
   */
  @Prop({ attribute: 'selected-language' }) readonly selectedLanguage?: LANGUAGE_CODE;
  /**
   * The 12-hour time format.
   */
  @Prop() hour12?: boolean;

  @State() startTime: string = '';
  @State() endTime: string = '';

  @Watch('themeConfig')
  themeConfigChanged(newThemeConfig: ThemeConfig) {
    this.applyThemeConfig(newThemeConfig);
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
    this.updateTimes();
  }

  @Watch('selectedTimeslot')
  selectedTimeslotChangedHandler() {
    this.updateTimes();
  }

  @Watch('selectedTimezone')
  selectedTimezoneChangedHandler() {
    this.updateTimes();
  }

  @Watch('hour12')
  hour12ChangedHandler() {
    this.updateTimes();
  }

  connectedCallback() {
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  disconnectedCallback() {}

  async componentWillLoad() {
    debug(`[nylas-selected-event-card] Component will load`);
  }

  async componentDidLoad() {
    debug(`[nylas-selected-event-card] Component did load`);
    if (!this.selectedDate) {
      debug('[nylas-selected-event-card] "selectedDate" prop is not provided.');
    }
    if (!this.selectedTimeslot) {
      debug('[nylas-selected-event-card] "selectedTimeslot" prop is not provided.');
    }
    this.updateTimes();
  }

  private updateTimes() {
    if (!this.selectedTimeslot?.start_time || !this.selectedTimeslot?.end_time) {
      this.startTime = '';
      this.endTime = '';
      return;
    }

    const locale = LANGUAGE_CODE_TO_LOCALE_MAP[this.selectedLanguage || 'en'];
    const timeFormat = new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: this.selectedTimezone,
      hour12: this.hour12,
    });

    this.startTime = timeFormat.format(new Date(this.selectedTimeslot.start_time));
    this.endTime = timeFormat.format(new Date(this.selectedTimeslot.end_time));
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  @RegisterComponent<NylasSelectedEventCard, NylasSchedulerConnector, Exclude<NylasScheduling['stores'], undefined>>({
    name: 'nylas-selected-event-card',
    stateToProps: new Map([
      ['scheduler.selectedDate', 'selectedDate'],
      ['scheduler.selectedTimeslot', 'selectedTimeslot'],
      ['scheduler.selectedTimezone', 'selectedTimezone'],
      ['scheduler.selectedLanguage', 'selectedLanguage'],
      ['scheduler.themeConfig', 'themeConfig'],
      ['scheduler.hour12', 'hour12'],
    ]),
    eventToProps: {},
    fireRegisterEvent: true,
  })
  render() {
    return (
      <Host part="nsec">
        <div class="event-card" part="nsec__card">
          <div class="calendar-icon" part="nsec__icon">
            <calendar-icon />
          </div>
          <div class="nylas-selected-event-card__date" part="nsec__date">
            {this.selectedDate
              ? capitalizeFirstLetter(
                  formatBookedEventDate(this.selectedTimeslot?.start_time as Date, this.selectedTimezone, LANGUAGE_CODE_TO_LOCALE_MAP[this.selectedLanguage || 'en']),
                )
              : '-'}
          </div>
          <div class="nylas-selected-event-card__time" part="nsec__time">
            {this.startTime} - {this.endTime}
          </div>
        </div>
        <div class="event-timezone" part="nsec__timezone">
          <globe-icon></globe-icon>
          {TIMEZONE_MAP[this.selectedTimezone]}
        </div>
      </Host>
    );
  }
}
